
import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CategoryGroup, getFinalLineItemAmount, getFinalLineItemUnitCost } from '@/utils/estimateExportUtils';
import { formatCurrency } from '@/lib/utils';
import { getDisplayDescription } from '@/utils/lineItemDisplayHelpers';

interface EstimateLineItemsTableProps {
  categorizedLineItems: CategoryGroup[];
  showSingleTable?: boolean;
  estimate?: {
    tax_percentage: number;
    tax_inclusive?: boolean;
  };
  taxAmount?: number;
  totalAmount?: number;
  isVatRegistered?: boolean;
  companyVatRate?: number;
  discountAmount?: number;
  discountPercentage?: number;
  getUnitSymbol: (unit: string) => string;
}

const EstimateLineItemsTable: React.FC<EstimateLineItemsTableProps> = ({ 
  categorizedLineItems, 
  showSingleTable = false,
  estimate,
  taxAmount = 0,
  totalAmount = 0,
  isVatRegistered = false,
  companyVatRate = 15,
  discountAmount,
  discountPercentage,
  getUnitSymbol
}) => {
  // Helper function to get proper header name for categories
  const getCategoryHeaderName = (categoryName: string) => {
    switch (categoryName) {
      case 'Labour':
        return 'Description of Work';
      case 'Materials':
        return 'Materials';
      case 'Other':
        return 'Other';
      case 'All-Inclusive Cost':
        return 'All-Inclusive Cost';
      default:
        return categoryName;
    }
  };

  if (showSingleTable && categorizedLineItems.length === 1) {
    const category = categorizedLineItems[0];
    
    // Show VAT line if company is VAT registered and has VAT rate > 0
    const showVat = isVatRegistered && companyVatRate > 0;
    
    return (
      <div className="mb-8">
        <Table className="mb-4 w-full estimate-table" style={{ tableLayout: 'fixed' }}>
          <TableHeader>
            <TableRow>
              <TableHead className="text-left border-table-cell" style={{ fontSize: '10px', fontWeight: 'bold', lineHeight: '1.15', width: '60% !important', whiteSpace: 'nowrap', padding: '8px 16px' }}>
                {getCategoryHeaderName(category.categoryName)}
              </TableHead>
              <TableHead className="text-right whitespace-nowrap border-table-cell" style={{ fontSize: '10px', fontWeight: 'bold', lineHeight: '1.15', width: '18% !important', padding: '8px 16px' }}>Unit Price</TableHead>
              <TableHead className="text-center whitespace-nowrap border-table-cell" style={{ fontSize: '10px', fontWeight: 'bold', lineHeight: '1.15', width: '7% !important', padding: '4px !important', maxWidth: '7% !important' }}>Unit</TableHead>
              <TableHead className="text-center whitespace-nowrap border-table-cell" style={{ fontSize: '10px', fontWeight: 'bold', lineHeight: '1.15', width: '9% !important', padding: '4px !important', maxWidth: '9% !important' }}>Quantity</TableHead>
              <TableHead className="text-right whitespace-nowrap border-table-cell" style={{ fontSize: '10px', fontWeight: 'bold', lineHeight: '1.15', width: '14% !important', padding: '8px 16px' }}>Amount Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {category.lineItems.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="text-left border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15', wordWrap: 'break-word', width: '60% !important', padding: '8px 16px' }}>{getDisplayDescription(item)}</TableCell>
                <TableCell className="text-right whitespace-nowrap border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15', width: '18% !important', padding: '8px 16px' }}>
                  {formatCurrency(getFinalLineItemUnitCost(item))}
                </TableCell>
                <TableCell className="text-center border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15', width: '7% !important', padding: '4px !important', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '7% !important' }}>
                  {getUnitSymbol(item.base_unit)}
                </TableCell>
                <TableCell className="text-center border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15', width: '9% !important', padding: '4px !important', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '9% !important' }}>{item.quantity_in_base_units || item.quantity}</TableCell>
                <TableCell className="text-right font-medium whitespace-nowrap border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15', fontWeight: '500', width: '14% !important', padding: '8px 16px' }}>
                  {formatCurrency(getFinalLineItemAmount(item))}
                </TableCell>
              </TableRow>
            ))}
            {/* Subtotal Row with merged columns */}
            <TableRow>
              <TableCell 
                colSpan={4} 
                className="text-left font-semibold border-table-cell" 
                style={{ fontSize: '10px', lineHeight: '1.15', fontWeight: 'bold' }}
              >
                Subtotal
              </TableCell>
              <TableCell className="text-right font-semibold whitespace-nowrap border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15', fontWeight: 'bold' }}>
                {formatCurrency(category.categoryTotal)}
              </TableCell>
            </TableRow>
            {/* Discount Row with merged columns */}
            {(discountAmount ?? 0) > 0 && (
              <TableRow>
                <TableCell 
                  colSpan={4} 
                  className="text-left border-table-cell" 
                  style={{ fontSize: '10px', lineHeight: '1.15' }}
                >
                  Discount (-{discountPercentage}%)
                </TableCell>
                <TableCell className="text-right whitespace-nowrap border-table-cell text-primary" style={{ fontSize: '10px', lineHeight: '1.15' }}>
                  -{formatCurrency(discountAmount)}
                </TableCell>
              </TableRow>
            )}
            {/* VAT Row if applicable with merged columns */}
            {showVat && (
              <TableRow>
                <TableCell 
                  colSpan={4} 
                  className="text-left border-table-cell" 
                  style={{ fontSize: '10px', lineHeight: '1.15' }}
                >
                  VAT ({companyVatRate}%)
                </TableCell>
                <TableCell className="text-right whitespace-nowrap border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15' }}>
                  {formatCurrency(taxAmount)}
                </TableCell>
              </TableRow>
            )}
            {/* Total Row with merged columns */}
            <TableRow>
              <TableCell 
                colSpan={4} 
                className="text-left font-bold border-table-cell" 
                style={{ fontSize: '10px', lineHeight: '1.15', fontWeight: 'bold' }}
              >
                Total
              </TableCell>
              <TableCell className="text-right font-bold whitespace-nowrap border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15', fontWeight: 'bold' }}>
                {formatCurrency(totalAmount)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <div className="mb-8">
      {categorizedLineItems.map((category, categoryIndex) => (
        <div key={categoryIndex} className="mb-8">
          {/* Category Line Items */}
          <Table className="mb-4 w-full estimate-table" style={{ tableLayout: 'fixed' }}>
            <TableHeader>
              <TableRow>
                <TableHead className="text-left border-table-cell" style={{ fontSize: '10px', fontWeight: 'bold', lineHeight: '1.15', width: '60% !important', whiteSpace: 'nowrap', padding: '8px 16px' }}>
                  {getCategoryHeaderName(category.categoryName)}
                </TableHead>
                <TableHead className="text-right whitespace-nowrap border-table-cell" style={{ fontSize: '10px', fontWeight: 'bold', lineHeight: '1.15', width: '18% !important', padding: '8px 16px' }}>Unit Price</TableHead>
                <TableHead className="text-center whitespace-nowrap border-table-cell" style={{ fontSize: '10px', fontWeight: 'bold', lineHeight: '1.15', width: '7% !important', padding: '4px !important', maxWidth: '7% !important' }}>Unit</TableHead>
                <TableHead className="text-center whitespace-nowrap border-table-cell" style={{ fontSize: '10px', fontWeight: 'bold', lineHeight: '1.15', width: '9% !important', padding: '4px !important', maxWidth: '9% !important' }}>Quantity</TableHead>
                <TableHead className="text-right whitespace-nowrap border-table-cell" style={{ fontSize: '10px', fontWeight: 'bold', lineHeight: '1.15', width: '14% !important', padding: '8px 16px' }}>Amount Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {category.lineItems.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="text-left border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15', wordWrap: 'break-word', width: '60% !important', padding: '8px 16px' }}>{getDisplayDescription(item)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15', width: '18% !important', padding: '8px 16px' }}>
                    {formatCurrency(getFinalLineItemUnitCost(item))}
                  </TableCell>
                  <TableCell className="text-center border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15', width: '7% !important', padding: '4px !important', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '7% !important' }}>
                    {getUnitSymbol(item.base_unit)}
                  </TableCell>
                  <TableCell className="text-center border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15', width: '9% !important', padding: '4px !important', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '9% !important' }}>{item.quantity_in_base_units || item.quantity}</TableCell>
                  <TableCell className="text-right font-medium whitespace-nowrap border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15', fontWeight: '500', width: '14% !important', padding: '8px 16px' }}>
                    {formatCurrency(getFinalLineItemAmount(item))}
                  </TableCell>
                </TableRow>
              ))}
              {/* Category Total Row with merged columns */}
              <TableRow>
                <TableCell 
                  colSpan={4} 
                  className="text-left font-semibold border-table-cell" 
                  style={{ fontSize: '10px', lineHeight: '1.15', fontWeight: 'bold' }}
                >
                  Total
                </TableCell>
                <TableCell className="text-right font-bold whitespace-nowrap border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15', fontWeight: 'bold' }}>
                  {formatCurrency(category.categoryTotal)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      ))}
    </div>
  );
};

export default EstimateLineItemsTable;
